import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, MapPin, Phone, Mail, Calendar, FileText, Pencil, Plus,
  Trash2, CheckCircle2, Circle, X, Save, ClipboardList, NotebookPen,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatDateBE, parseDate } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/lib/auth";

const TIMEFRAMES = [
  { key: "3m",      label: "3 maanden",   accent: "ready" },
  { key: "6m",      label: "6 maanden",   accent: "ready" },
  { key: "1y",      label: "1 jaar",      accent: "ripening" },
  { key: "2y",      label: "2 jaar",      accent: "ripening" },
  { key: "3y_plus", label: "3+ jaar",     accent: "fresh" },
  { key: "unknown", label: "Onbepaald",   accent: "fresh" },
];

function toIsoInput(s) {
  const d = parseDate(s);
  if (!d || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromIsoInput(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

export default function FollowThroughDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [savingTask, setSavingTask] = useState(false);
  const [confirmNote, setConfirmNote] = useState(null);
  const [confirmDeleteEntry, setConfirmDeleteEntry] = useState(false);
  const [editingOverview, setEditingOverview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get(`/api/follow-through/${id}`);
      setEntry(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kon dossier niet laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const currentTf = useMemo(
    () => TIMEFRAMES.find(x => x.key === entry?.current_timeframe),
    [entry],
  );
  const storedTf = useMemo(
    () => TIMEFRAMES.find(x => x.key === entry?.timeframe),
    [entry],
  );

  const addNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      await http.post(`/api/follow-through/${id}/notes`, { text });
      setNoteDraft("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSavingNote(false);
    }
  };

  const removeNote = async () => {
    const noteId = confirmNote?.id;
    setConfirmNote(null);
    if (!noteId) return;
    try {
      await http.delete(`/api/follow-through/${id}/notes/${noteId}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title) return;
    setSavingTask(true);
    try {
      await http.post("/api/tasks", {
        title,
        follow_through_id: id,
        due_date: newTaskDue ? fromIsoInput(newTaskDue) : null,
      });
      setNewTaskTitle("");
      setNewTaskDue("");
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Taak aanmaken mislukt");
    } finally {
      setSavingTask(false);
    }
  };

  const toggleTask = async (task) => {
    try {
      await http.patch(`/api/tasks/${task.id}`, { completed: !task.completed });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Bijwerken mislukt");
    }
  };

  const deleteTask = async (task) => {
    try {
      await http.delete(`/api/tasks/${task.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const deleteEntry = async () => {
    setConfirmDeleteEntry(false);
    try {
      await http.delete(`/api/follow-through/${id}`);
      navigate("/follow-through");
    } catch (e) {
      alert(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (loading) {
    return <div className="p-8 text-[var(--ink-500)]" data-testid="follow-detail-loading">{t("common.loading")}</div>;
  }
  if (error || !entry) {
    return (
      <div className="p-8" data-testid="follow-detail-error">
        <button type="button" onClick={() => navigate("/follow-through")}
                className="btn btn-ghost h-9 mb-4"><ArrowLeft size={14} /> Terug</button>
        <div className="card text-[var(--danger)]">{error || "Niet gevonden"}</div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 p-8 overflow-auto" data-testid="follow-detail-screen">
      <button type="button" onClick={() => navigate("/follow-through")}
              data-testid="btn-follow-detail-back"
              className="btn btn-ghost h-9 mb-4">
        <ArrowLeft size={14} /> Terug naar Follow-through
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             ${currentTf?.accent === "ready" ? "chip-teal"
                               : currentTf?.accent === "ripening" ? "bg-[#FCEBC9] text-[#7C5A1E] border-[#E5C9A0]"
                               : "bg-[var(--sand-100)] text-[var(--ink-700)] border-[var(--border-soft)]"}`}>
              {currentTf?.label || "Onbepaald"}
            </span>
            {storedTf && entry.timeframe !== "unknown"
              && entry.current_timeframe !== entry.timeframe && (
                <span className="text-[10px] text-[var(--ink-400)] uppercase tracking-wider">
                  ← was {storedTf.label}
                </span>
              )}
          </div>
          <h1 className="font-display text-[28px] leading-tight text-[var(--ink-900)] flex items-center gap-2"
              data-testid="follow-detail-address">
            <MapPin size={18} className="text-[var(--ink-400)]" />
            {entry.address}
          </h1>
          <div className="text-[13px] text-[var(--ink-500)] mt-1 ml-[26px]">
            {[entry.postal_code, entry.city].filter(Boolean).join(" ") || "—"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setEditingOverview(true)}
                  data-testid="btn-follow-detail-edit"
                  className="btn btn-ghost h-9 !px-3">
            <Pencil size={14} /> Bewerken
          </button>
          <button type="button" onClick={() => setConfirmDeleteEntry(true)}
                  data-testid="btn-follow-detail-delete"
                  className="btn btn-ghost h-9 !px-3 text-[#B43232] hover:!bg-[#FBE7E7]">
            <Trash2 size={14} /> Verwijderen
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overview & contact */}
        <div className="lg:col-span-1 space-y-4">
          <section className="card" data-testid="follow-detail-overview">
            <h2 className="font-display text-[16px] mb-3 text-[var(--ink-900)]">Overzicht</h2>
            <dl className="space-y-2 text-[13px]">
              <Row label="Tijdschatting" value={storedTf?.label || "—"} />
              <Row label="Huidige bucket" value={currentTf?.label || "—"} />
              <Row label="Referentiedatum"
                   value={entry.reference_date ? formatDateBE(entry.reference_date) : "—"} />
              <Row label="Aangemaakt door" value={entry.created_by_name || "—"} />
            </dl>
          </section>

          <section className="card" data-testid="follow-detail-contact">
            <h2 className="font-display text-[16px] mb-3 text-[var(--ink-900)]">Eigenaar</h2>
            {entry.owner_name || entry.owner_phone || entry.owner_email ? (
              <div className="space-y-2 text-[13px]">
                {entry.owner_name && (
                  <div className="font-semibold text-[var(--ink-900)]">{entry.owner_name}</div>
                )}
                {entry.owner_phone && (
                  <a href={`tel:${entry.owner_phone}`}
                     className="flex items-center gap-2 text-[var(--ink-700)] hover:text-[var(--teal-600)]">
                    <Phone size={13} /> {entry.owner_phone}
                  </a>
                )}
                {entry.owner_email && (
                  <a href={`mailto:${entry.owner_email}`}
                     className="flex items-center gap-2 text-[var(--ink-700)] hover:text-[var(--teal-600)] break-all">
                    <Mail size={13} /> {entry.owner_email}
                  </a>
                )}
              </div>
            ) : (
              <div className="text-[13px] text-[var(--ink-400)] italic">Geen contactgegevens.</div>
            )}
          </section>

          {entry.notes && (
            <section className="card" data-testid="follow-detail-base-notes">
              <h2 className="font-display text-[16px] mb-2 text-[var(--ink-900)] flex items-center gap-2">
                <FileText size={14} /> Achtergrond
              </h2>
              <p className="text-[13px] text-[var(--ink-700)] whitespace-pre-wrap leading-relaxed">
                {entry.notes}
              </p>
            </section>
          )}
        </div>

        {/* Tasks + notes feed */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tasks */}
          <section className="card" data-testid="follow-detail-tasks">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-[16px] text-[var(--ink-900)] flex items-center gap-2">
                <ClipboardList size={15} /> Taken
                <span className="text-[12px] text-[var(--ink-500)] font-normal">
                  ({(entry.tasks || []).filter(t => !t.completed).length} open)
                </span>
              </h2>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <input
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addTask(); } }}
                placeholder="Bv. Nabellen volgende maand…"
                data-testid="follow-detail-task-title"
                className="flex-1 min-w-[200px] h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]"
              />
              <input
                type="date"
                value={newTaskDue}
                onChange={(e) => setNewTaskDue(e.target.value)}
                data-testid="follow-detail-task-due"
                className="h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]"
              />
              <button type="button" onClick={addTask}
                      disabled={!newTaskTitle.trim() || savingTask}
                      data-testid="btn-follow-detail-add-task"
                      className="btn btn-primary h-10 !px-4 disabled:opacity-50 disabled:cursor-not-allowed">
                <Plus size={14} /> {savingTask ? "Opslaan…" : "Taak toevoegen"}
              </button>
            </div>
            {(entry.tasks || []).length === 0 ? (
              <div className="text-[13px] text-[var(--ink-400)] italic py-3">Nog geen taken.</div>
            ) : (
              <ul className="divide-y divide-[var(--border-soft)]">
                {entry.tasks.map(task => (
                  <li key={task.id}
                      data-testid={`follow-detail-task-${task.id}`}
                      className="py-3 flex items-start gap-3">
                    <button type="button" onClick={() => toggleTask(task)}
                            data-testid={`btn-task-toggle-${task.id}`}
                            className={`mt-0.5 ${task.completed ? "text-[var(--teal-600)]" : "text-[var(--ink-400)] hover:text-[var(--teal-600)]"}`}>
                      {task.completed ? <CheckCircle2 size={17} /> : <Circle size={17} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[14px] ${task.completed ? "line-through text-[var(--ink-400)]" : "text-[var(--ink-900)]"}`}>
                        {task.title}
                      </div>
                      <div className="text-[11px] text-[var(--ink-500)] mt-0.5 flex items-center gap-2 flex-wrap">
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={11} /> {formatDateBE(task.due_date)}
                          </span>
                        )}
                        {task.created_by?.name && <span>· door {task.created_by.name}</span>}
                      </div>
                    </div>
                    <button type="button" onClick={() => deleteTask(task)}
                            data-testid={`btn-task-delete-${task.id}`}
                            title="Verwijderen"
                            className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
                      <Trash2 size={13} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Notes feed */}
          <section className="card" data-testid="follow-detail-notes">
            <h2 className="font-display text-[16px] mb-3 text-[var(--ink-900)] flex items-center gap-2">
              <NotebookPen size={15} /> Notities
              <span className="text-[12px] text-[var(--ink-500)] font-normal">
                ({(entry.notes_log || []).length})
              </span>
            </h2>
            <div className="flex flex-col gap-2 mb-4">
              <textarea
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Schrijf een notitie… (gesprek, observatie, afspraak)"
                rows={3}
                data-testid="follow-detail-note-input"
                className="w-full px-3 py-2 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)] resize-y"
              />
              <div className="flex justify-end">
                <button type="button" onClick={addNote}
                        disabled={!noteDraft.trim() || savingNote}
                        data-testid="btn-follow-detail-add-note"
                        className="btn btn-primary h-9 !px-4 disabled:opacity-50 disabled:cursor-not-allowed">
                  <Plus size={14} /> {savingNote ? "Opslaan…" : "Notitie toevoegen"}
                </button>
              </div>
            </div>
            {(entry.notes_log || []).length === 0 ? (
              <div className="text-[13px] text-[var(--ink-400)] italic">Nog geen notities.</div>
            ) : (
              <ul className="space-y-3">
                {entry.notes_log.map(n => (
                  <li key={n.id}
                      data-testid={`follow-note-${n.id}`}
                      className="p-3 rounded-lg border border-[var(--border-soft)] bg-[var(--sand-50)]/70">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-[11px] text-[var(--ink-500)]">
                        {n.author_name || "Onbekend"} · {new Date(n.created_at).toLocaleString("nl-BE", {
                          day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                      </div>
                      {(me?.role === "super_admin" || me?.role === "tenant_admin"
                        || n.author_user_id === me?.user_id || n.author_user_id === me?.id) && (
                        <button type="button" onClick={() => setConfirmNote(n)}
                                data-testid={`btn-note-delete-${n.id}`}
                                title="Verwijderen"
                                className="p-1 rounded-md text-[var(--ink-400)] hover:text-[#B43232] hover:bg-[#FBE7E7]">
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                    <div className="text-[14px] text-[var(--ink-900)] whitespace-pre-wrap leading-relaxed">
                      {n.text}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {editingOverview && (
        <EditOverviewDialog
          entry={entry}
          onClose={() => setEditingOverview(false)}
          onSaved={() => { setEditingOverview(false); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmNote}
        danger
        title="Notitie verwijderen"
        message="Deze notitie definitief verwijderen?"
        confirmLabel="Verwijderen"
        onConfirm={removeNote}
        onCancel={() => setConfirmNote(null)}
      />

      <ConfirmDialog
        open={confirmDeleteEntry}
        danger
        title="Dossier verwijderen"
        message={`Weet u zeker dat u "${entry.address}" wilt verwijderen? Notities en gekoppelde taken worden ontkoppeld.`}
        confirmLabel="Verwijderen"
        onConfirm={deleteEntry}
        onCancel={() => setConfirmDeleteEntry(false)}
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-[11px] uppercase tracking-wider text-[var(--ink-500)]">{label}</dt>
      <dd className="text-[var(--ink-900)] font-medium text-right">{value}</dd>
    </div>
  );
}

function EditOverviewDialog({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({
    address: entry.address || "",
    postal_code: entry.postal_code || "",
    city: entry.city || "",
    owner_name: entry.owner_name || "",
    owner_phone: entry.owner_phone || "",
    owner_email: entry.owner_email || "",
    timeframe: entry.timeframe || "unknown",
    reference_date: entry.reference_date || "",
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const cls = "w-full h-10 px-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm outline-none focus:border-[var(--teal-500)]";

  const save = async () => {
    if (!form.address.trim()) return;
    setSaving(true);
    try {
      await http.patch(`/api/follow-through/${entry.id}`, form);
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 backdrop-blur-sm p-4"
         data-testid="follow-detail-edit-dialog">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[560px] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-[var(--border-soft)]">
          <h2 className="font-display text-[20px]">Dossier bewerken</h2>
          <button type="button" onClick={onClose}
                  className="p-1.5 rounded-md text-[var(--ink-500)] hover:bg-[var(--sand-100)]">
            <X size={16} />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          <FieldLabel label="Adres *">
            <input value={form.address} onChange={set("address")} className={cls} autoFocus />
          </FieldLabel>
          <div className="grid grid-cols-3 gap-3">
            <FieldLabel label="Postcode">
              <input value={form.postal_code} onChange={set("postal_code")} className={cls} />
            </FieldLabel>
            <div className="col-span-2">
              <FieldLabel label="Gemeente">
                <input value={form.city} onChange={set("city")} className={cls} />
              </FieldLabel>
            </div>
          </div>
          <FieldLabel label="Naam eigenaar">
            <input value={form.owner_name} onChange={set("owner_name")} className={cls} />
          </FieldLabel>
          <div className="grid grid-cols-2 gap-3">
            <FieldLabel label="Telefoon">
              <input value={form.owner_phone} onChange={set("owner_phone")} className={cls} />
            </FieldLabel>
            <FieldLabel label="E-mail">
              <input value={form.owner_email} onChange={set("owner_email")} type="email" className={cls} />
            </FieldLabel>
          </div>
          <FieldLabel label="Tijdschatting">
            <select value={form.timeframe} onChange={set("timeframe")} className={cls}>
              {TIMEFRAMES.map(tf => <option key={tf.key} value={tf.key}>{tf.label}</option>)}
            </select>
          </FieldLabel>
          <FieldLabel label="Referentiedatum">
            <input type="date"
                   value={toIsoInput(form.reference_date)}
                   onChange={(e) => setForm(f => ({ ...f, reference_date: fromIsoInput(e.target.value) }))}
                   className={cls} />
          </FieldLabel>
          <FieldLabel label="Achtergrond / context">
            <textarea value={form.notes} onChange={set("notes")} rows={3}
                      className={`${cls} !h-auto !py-2 resize-y`} />
          </FieldLabel>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-soft)] bg-[var(--sand-50)]">
          <button type="button" onClick={onClose} className="btn btn-ghost h-9 !px-3">
            {t("common.cancel")}
          </button>
          <button type="button" onClick={save}
                  disabled={!form.address.trim() || saving}
                  data-testid="btn-follow-detail-save"
                  className="btn btn-primary h-9 !px-4 disabled:opacity-50 disabled:cursor-not-allowed">
            <Save size={14} /> {saving ? "Opslaan…" : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-medium text-[var(--ink-700)] mb-1 uppercase tracking-wider">{label}</div>
      {children}
    </label>
  );
}
